import React, { useState, useEffect } from "react";
import { MobileCameraCapture } from "./take-picture";
import { RecordAudio } from "./record-audio";
import { ActionButtons } from "../atoms/action-buttons";
import { useSubmission } from "../../providers/submission-provider";
import { useTasks } from "../../providers/task-provider";

interface MediaCaptureProps {
  onMediaTaken: (pictureFile: File, audioFile: File) => void;
  onGoBack: () => void;
  audioName: string;
}

export const MediaCapture: React.FC<MediaCaptureProps> = ({
  audioName,
  onGoBack,
  onMediaTaken,
}) => {
  const [pictureTimestamp, setPictureTimestamp] = useState<number | null>(null);
  const [isAudioAllowed, setIsAudioAllowed] = useState(false);
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  const [checkInterval, setCheckInterval] = useState<any | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const { submission } = useSubmission();
  const { setCurrentTask, currentTask } = useTasks();

  useEffect(() => {
    if (pictureTimestamp) {
      const interval = setInterval(() => {
        const timeElapsed = Date.now() - pictureTimestamp;
        if (timeElapsed > 30000) {
          if (!isAudioRecording) {
            setIsAudioAllowed(false);
            setPictureFile(null);
            setAudioFile(null);
          }
          clearInterval(interval);
        }
      }, 1000);
      setCheckInterval(interval);
      return () => clearInterval(interval);
    }
  }, [pictureTimestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pictureFile && audioFile) {
      onMediaTaken(pictureFile, audioFile);
    }
  }, [pictureFile, audioFile, onMediaTaken]);

  useEffect(() => {
    if (checkInterval && isAudioRecording) {
      clearInterval(checkInterval);
    }
  }, [checkInterval, isAudioRecording]);

  const handlePictureTaken = (timestamp: number, pictureFile: File) => {
    setPictureTimestamp(timestamp);
    setIsAudioAllowed(true);
    setPictureFile(pictureFile);
  };

  const handleAudioFinished = (audioFile: File) => {
    console.log("Audio file received:", audioFile);
    setAudioFile(audioFile);
  };

  return (
    <div className="media-wrapper">
      <h2 className="text-left mb-2">
        Record: {audioName}
        {(currentTask?.subcategories?.length || 0) > 1
          ? `- ${submission?.submission_metadata["sub_category"]}`
          : ""}
      </h2>
      <div className="rounded-xl border-dark-gray-1 border text-left p-4 mb-2">
        <h3 className="font-medium mb-2">Instructions:</h3>
        <ol className="text-sm list-decimal list-inside">
          <li>Take picture of the recording subject.</li>
          <li>After picture taken, record audio with in 30 seconds.</li>
          <li>Ensure minimal background noise.</li>
          <li>Microphone access should be enable for the current browser.</li>
          <li>Location services should be enable for the current browser.</li>
          <li>Record audio naturally.</li>
          <li>Minimum 3 seconds duration.</li>
          <li>Record in natural environment.</li>
        </ol>
      </div>
      {isAudioAllowed ? (
        <RecordAudio
          onRecordingFinished={handleAudioFinished}
          onRecordingStart={() => setIsAudioRecording(true)}
        />
      ) : (
        <>
          <p className="text-sm text-red-500 mb-2">
            Record audio will be available after taking a picture within 30
            seconds.
          </p>
          <MobileCameraCapture
            onPictureTaken={handlePictureTaken}
            showPreview={false}
          />
        </>
      )}
      <div className="mt-6">
        <ActionButtons
          primaryText=""
          secondaryText="Go Back"
          onSecondaryClick={() => {
            setCurrentTask(null);
            onGoBack();
          }}
          onPrimaryClick={() => null}
        />
      </div>
    </div>
  );
};
